/**
 * Decisions on this device (docs/ux/project-tab-spec.md §1.6, §2.5, §3.4). The asking machine's
 * daemon writes the sealed `decision` entity; this device reads it, appends the user's thread
 * messages (optimistically, merged by id) and signs `decision.message` / `decision.answer`
 * (`decision.answer.risky` for risky categories: a passkey step-up). The hub sees only
 * ciphertext entity revisions and sealed commands addressed by machine id.
 *
 * Routing: the asking run's machine when it is online, else the project's triage machine with
 * `via: 'triage'` (the run cannot be reached; the triage agent picks the thread up).
 */
import {
  type BarkAttachment,
  type CommandKind,
  type CommandPayload,
  type DecisionDelivery,
  type DecisionRecord,
  decisionBody,
  hydrateDecision,
  mergeDecision,
} from '@viko/protocol'
import { type AnswerInput, answerCommand, type DecisionView } from './decision-view.js'
import type { Machine } from './types.js'

export interface DecisionEntities {
  list(kind: 'decision'): Promise<{ id: string; value: Record<string, unknown> }[]>
  read(kind: 'decision', id: string): Promise<{ id: string; value: Record<string, unknown> }>
  write(kind: 'decision', id: string, value: Record<string, unknown>): Promise<unknown>
}

type DecisionKind = Extract<
  CommandKind,
  'decision.message' | 'decision.answer' | 'decision.answer.risky'
>

export interface DecisionBackend {
  /** Undefined while locked (no content key on this device). */
  entities(): DecisionEntities | undefined
  dispatch<K extends DecisionKind>(
    kind: K,
    machineId: string,
    payload: CommandPayload<K>,
    opts?: { summary?: string },
  ): Promise<{ result?: unknown } | unknown>
  deviceKeyId?(): string | undefined
}

export interface Route {
  machineId: string
  via?: 'triage'
}

/** Where a thread message or answer goes (see the module comment). */
export function decisionRoute(
  d: Pick<DecisionRecord, 'machineId'>,
  machines: readonly Pick<Machine, 'id' | 'online'>[],
  triageMachine: string | undefined,
): Route | undefined {
  const asking = d.machineId ? machines.find((m) => m.id === d.machineId) : undefined
  if (asking?.online) return { machineId: asking.id }
  if (triageMachine && triageMachine !== d.machineId)
    return { machineId: triageMachine, via: 'triage' }
  // Commands queue for an offline daemon (a sleeping PC) for up to a day.
  return d.machineId ? { machineId: d.machineId } : undefined
}

const deliveryOf = (r: unknown): DecisionDelivery['status'] | undefined => {
  const x = (r as { result?: { delivery?: unknown } } | undefined)?.result?.delivery
  return typeof x === 'string' ? (x as DecisionDelivery['status']) : undefined
}

export class DecisionStore {
  private readonly local = new Map<string, DecisionRecord>()
  private readonly listeners = new Set<() => void>()
  private revision = 0

  constructor(
    private readonly be: DecisionBackend,
    private readonly now: () => number = Date.now,
  ) {}

  /** The app's project id for a decision (identity on encrypted accounts; the mock maps slugs). */
  projectOf: (d: Pick<DecisionRecord, 'projectId'>) => string = (d) => d.projectId

  subscribe = (cb: () => void) => {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }
  version = () => this.revision
  protected emit() {
    this.revision++
    for (const l of this.listeners) l()
  }

  get available(): boolean {
    return !!this.be.entities()
  }

  /** Every decision this account can read; optimistic local writes win until the relay has them. */
  async list(): Promise<DecisionRecord[]> {
    const ents = this.be.entities()
    const rows = ents ? await ents.list('decision') : []
    const out = new Map<string, DecisionRecord>()
    for (const r of rows) {
      try {
        const d = hydrateDecision(r)
        out.set(d.id, d)
      } catch {
        /* a malformed or foreign record is skipped, never shown */
      }
    }
    for (const d of this.local.values()) {
      const remote = out.get(d.id)
      if (!remote || remote.updatedAt < d.updatedAt)
        out.set(d.id, remote ? mergeDecision(remote, d) : d)
      else this.local.delete(d.id)
    }
    return [...out.values()].sort((a, b) => b.createdAt - a.createdAt)
  }

  async get(id: string): Promise<DecisionRecord | undefined> {
    return (await this.list()).find((d) => d.id === id)
  }

  private async put(next: DecisionRecord) {
    this.local.set(next.id, next)
    this.emit()
    const ents = this.be.entities()
    if (!ents) return
    try {
      const latest = hydrateDecision(await ents.read('decision', next.id))
      await ents.write('decision', next.id, decisionBody(mergeDecision(latest, next)))
    } catch {
      /* the daemon appends the message too (idempotent by id); the local copy shows meanwhile */
    }
  }

  /** A thread message: shown at once, then delivered by the asking machine (or triage). */
  async message(
    d: DecisionRecord,
    text: string,
    route: Route,
    attachments: BarkAttachment[] = [],
    messageId = `u_${crypto.randomUUID()}`,
  ): Promise<DecisionDelivery['status']> {
    const at = this.now()
    const msg = {
      id: messageId,
      at,
      author: 'user' as const,
      text,
      ...(attachments.length ? { attachments } : {}),
    }
    let base = (await this.get(d.id)) ?? d
    base = { ...base, thread: [...base.thread, msg].slice(-200), updatedAt: at }
    await this.put(base)
    const mark = async (delivery: DecisionDelivery) => {
      const cur = (await this.get(d.id)) ?? base
      await this.put({
        ...cur,
        thread: cur.thread.map((m) => (m.id === messageId ? { ...m, delivery } : m)),
        updatedAt: this.now(),
      })
    }
    try {
      const r = await this.be.dispatch('decision.message', route.machineId, {
        decisionId: d.id,
        projectId: d.projectId,
        messageId,
        text,
        ...(attachments.length ? { attachments } : {}),
        ...(route.via ? { via: route.via } : {}),
      })
      const status = deliveryOf(r) ?? 'sent'
      await mark({ status, at: this.now() })
      return status
    } catch (e) {
      await mark({
        status: 'failed',
        at: this.now(),
        note: e instanceof Error ? e.message.slice(0, 200) : 'Could not send',
      })
      return 'failed'
    }
  }

  /** Pick / freeform / decide-for-me. Risky categories sign the step-up kind. */
  async answer(
    v: DecisionView,
    a: AnswerInput,
    route: Route,
    o: { callId?: string } = {},
  ): Promise<{ commandId?: string }> {
    const d = v.decision
    if (!d) throw new Error('not a decision entity')
    const cmd = answerCommand(v, a, o)
    const out = await this.be.dispatch(cmd.kind, route.machineId, cmd.payload as never, {
      summary: `Answer "${v.title}"`,
    })
    const cur = (await this.get(d.id)) ?? d
    const at = this.now()
    await this.put({
      ...cur,
      status: a.mode === 'delegate' ? 'delegated' : 'answered',
      answer: {
        mode: a.mode,
        ...(a.mode === 'pick' ? { optionId: a.optionId } : {}),
        ...(a.mode === 'pick' && a.note?.trim() ? { text: a.note.trim() } : {}),
        ...(a.mode === 'pick' && a.mix?.length ? { mix: a.mix } : {}), // visual-review
        ...(a.mode === 'pick' && a.comments && Object.keys(a.comments).length
          ? { comments: a.comments }
          : {}),
        ...(a.mode === 'freeform' ? { text: a.text } : {}),
        ...(a.mode === 'delegate' && a.guidance?.trim() ? { guidance: a.guidance.trim() } : {}),
        at,
        deviceKeyId: this.be.deviceKeyId?.() ?? 'this-device',
        ...(o.callId ? { via: 'call' as const } : {}), // viko-call
      },
      updatedAt: at,
    })
    const commandId = (out as { commandId?: unknown } | undefined)?.commandId
    return typeof commandId === 'string' ? { commandId } : {}
  }

  /** "Later": synced across devices through the entity. */
  async snooze(d: DecisionRecord, until: number): Promise<void> {
    const cur = (await this.get(d.id)) ?? d
    await this.put({ ...cur, snoozedUntil: until, updatedAt: this.now() })
  }
}

/** One store per entity client (the account's content session). */
const stores = new WeakMap<object, DecisionStore>()
export function decisionStoreFor(key: object, make: () => DecisionStore): DecisionStore {
  let s = stores.get(key)
  if (!s) {
    s = make()
    stores.set(key, s)
  }
  return s
}
